import type {
  Action,
  ActionExample,
  HandlerCallback,
  IAgentRuntime,
  Memory,
  State,
} from '@elizaos/core';
import { ModelType, logger, parseKeyValueXml } from '@elizaos/core';
import type { ZapperConfig } from '../types';
import type {
  TransactionHistoryContent,
  TransactionHistoryData,
  TransactionHistoryResponse,
  TransactionPerspective,
  TransactionFilters,
} from '../types/transaction';
import { ZapperGraphQLClient, ZapperGraphQLUtils } from '../utils/zapperGraphQL';

// ✅ ADD standardized network mapping
const NETWORK_CHAIN_MAP: { [key: string]: number } = {
  ethereum: 1,
  polygon: 137,
  arbitrum: 42161,
  optimism: 10,
  base: 8453,
  avalanche: 43114,
  bsc: 56,
  fantom: 250,
  gnosis: 100,
  celo: 42220,
  moonbeam: 1284,
  aurora: 1313161554,
  harmony: 1666600000,
  moonriver: 1285,
  metis: 1088,
  cronos: 25,
  evmos: 9001,
  milkomeda: 2001,
  fuse: 122,
  smartbch: 10000,
  syscoin: 57,
  okex: 66,
  heco: 128,
  kava: 2222,
  klaytn: 8217,
  iotex: 4689,
  theta: 361,
  godwoken: 71402,
  kcc: 321,
  wanchain: 888,
  elastos: 20,
  meter: 82,
  rei: 47805,
  astar: 592,
  shiden: 336,
  karura: 686,
  acala: 787,
};

// ✅ REPLACE complex extractWalletAddress function with this simple version:
function extractWalletAddress(text: string, state?: State): string | null {
  logger.debug('🔍 Extracting wallet address', {
    textLength: text?.length || 0,
    hasState: !!state,
  });

  if (!text) return null;

  // Simple but reliable patterns for address extraction
  const patterns = [
    // Ethereum address: 0x followed by 40 hex characters
    /0x[a-fA-F0-9]{40}/,
    // ENS name: word characters, hyphens, followed by .eth
    /\b[\w\-]+\.eth\b/,
    // Basename: word characters, hyphens, followed by .base
    /\b[\w\-]+\.base\b/,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      logger.info('✅ Address found', {
        address: match[0],
        pattern: pattern.source,
      });
      return match[0];
    }
  }

  logger.debug('❌ No address found in text');
  return null;
}

const getTransactionHistoryTemplate = `Respond with an XML block containing only the extracted values. Use key-value pairs.

Example response for transaction history request:
<response>
    <address>0x1234567890abcdef1234567890abcdef12345678</address>
    <perspective>Signer</perspective>
    <networks>ethereum,polygon</networks>
    <first>20</first>
</response>

## Recent Messages

{{recentMessages}}

Given the recent messages, extract the following information about the transaction history request:
- Address: The wallet address to get transaction history for
- Perspective: Transaction perspective - Signer (sent), Receiver (received), or All (default: Signer)
- Networks: Optional comma-separated list of networks (ethereum, polygon, arbitrum, optimism, base)
- First: Number of transactions to fetch (default: 20, max: 50)
- After: Optional cursor for pagination
- TokenAddress: Optional comma-separated list of token addresses to filter by
- MinValue: Optional minimum transaction value in USD
- MaxValue: Optional maximum transaction value in USD

Respond with an XML block containing only the extracted values.`;

export const getTransactionHistoryAction: Action = {
  name: 'GET_TRANSACTION_HISTORY',
  similes: [
    'GET_TRANSACTIONS',
    'TRANSACTION_HISTORY',
    'SHOW_TRANSACTIONS',
    'VIEW_TRANSACTION_HISTORY',
    'LIST_TRANSACTIONS',
    'RECENT_TRANSACTIONS',
    'TRANSACTION_TIMELINE',
    'ACTIVITY_FEED',
  ],
  description: 'Get transaction history for a wallet address with filtering and pagination support',
  validate: async (runtime: IAgentRuntime, message: Memory, state?: State): Promise<boolean> => {
    logger.info('🔍 Starting GET_TRANSACTION_HISTORY validation');

    // Check API key
    const apiKey = runtime.getSetting('ZAPPER_API_KEY');
    if (!apiKey) {
      logger.error('❌ ZAPPER_API_KEY not configured');
      return false;
    }

    // Check for required data (address)
    const messageText = message.content.text || '';
    const address = extractWalletAddress(messageText, state);

    if (!address) {
      logger.warn('⚠️ No wallet address found for GET_TRANSACTION_HISTORY');
      return false;
    }

    logger.info('✅ Validation passed', {
      address,
      dataType: 'wallet_address',
    });

    return true;
  },
  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state?: State,
    options?: { [key: string]: unknown },
    callback?: HandlerCallback
  ): Promise<boolean> => {
    logger.info('🚀 Starting GET_TRANSACTION_HISTORY handler');

    try {
      // 1. Get configuration
      const apiKey = runtime.getSetting('ZAPPER_API_KEY');
      const apiUrl = runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql';

      if (!apiKey) {
        throw new Error('ZAPPER_API_KEY not configured');
      }

      // 2. State composition
      logger.log('📝 Step 1: Composing state...');
      let currentState = state;
      if (!currentState) {
        logger.debug('Creating new state from message');
        currentState = await runtime.composeState(message);
      } else {
        logger.debug('Using existing state, composing with recent messages');
        currentState = await runtime.composeState(message, ['RECENT_MESSAGES']);
      }

      // 3. Prompt preparation
      logger.log('🎯 Step 2: Preparing prompt...');
      const prompt = getTransactionHistoryTemplate.replace(
        '{{recentMessages}}',
        currentState.recentMessagesData || ''
      );

      // 4. Model processing
      logger.log('🤖 Step 3: Processing with language model...');
      const result = await runtime.useModel(ModelType.TEXT_SMALL, {
        prompt,
        stopSequences: [],
      });

      // 5. XML parsing
      logger.log('🔍 Step 4: Parsing XML content...');
      const content = parseKeyValueXml(result) as Partial<TransactionHistoryContent>;

      if (!isTransactionHistoryContent(runtime, content)) {
        const helpMessage = `I need a wallet address to get transaction history. Please provide:

• Ethereum address (e.g., 0x1234...5678)
• ENS name (e.g., vitalik.eth)
• Basename (e.g., example.base)

Try: "Show transaction history for vitalik.eth"`;

        callback?.({
          text: helpMessage,
          content: { error: 'No wallet address provided' },
        });
        return false;
      }

      logger.info('📍 Using address for lookup', { address: content.address });

      // 3. Initialize GraphQL client
      const client = new ZapperGraphQLClient({
        apiKey,
        apiUrl,
        debug: true,
        timeout: 30000,
        retryAttempts: 3,
      });

      // 4. Parse additional parameters
      const perspective: TransactionPerspective = content.perspective || 'Signer';

      // Parse networks/chainIds
      let chainIds: number[] = [];
      if (content.networks) {
        let networks: string[] = [];
        if (typeof content.networks === 'string') {
          networks = content.networks.split(',').map((n: string) => n.trim());
        } else if (Array.isArray(content.networks)) {
          networks = content.networks;
        }

        chainIds = networks.map((network) => {
          const normalized = network.toLowerCase().trim();
          return NETWORK_CHAIN_MAP[normalized] || 1; // Default to Ethereum if unknown
        });
      }

      if (content.chainIds) {
        if (Array.isArray(content.chainIds)) {
          chainIds = content.chainIds.map((id: unknown) =>
            typeof id === 'string' ? Number.parseInt(id, 10) : (id as number)
          );
        } else if (typeof content.chainIds === 'string') {
          const chainIdString = content.chainIds as string;
          chainIds = chainIdString.split(',').map((id: string) => Number.parseInt(id.trim(), 10));
        } else if (typeof content.chainIds === 'number') {
          chainIds = [content.chainIds];
        }
      }

      // 5. Prepare GraphQL query and variables
      const query = `
        query GetTransactionHistory(
          $subjects: [Address!]!
          $perspective: TransactionHistoryV2Perspective!
          $first: Int!
          $after: String
          $filters: TransactionHistoryV2FiltersArgs
        ) {
          transactionHistoryV2(
            subjects: $subjects
            perspective: $perspective
            first: $first
            after: $after
            filters: $filters
          ) {
            edges {
              node {
                ... on TimelineEventV2 {
                  hash
                  timestamp
                  network
                  fromAddress {
                    address
                  }
                  toAddress {
                    address
                  }
                  methodSighash
                  value
                  gas
                  interpretation {
                    processedDescription
                    description
                  }
                  app {
                    displayName
                    websiteUrl
                  }
                }
              }
              cursor
            }
            pageInfo {
              hasNextPage
              endCursor
            }
            totalCount
          }
        }
      `;

      const filters: Partial<TransactionFilters> = {};
      if (chainIds.length > 0) {
        filters.chainIds = chainIds;
      }
      if (content.tokenAddress) {
        filters.tokenAddress = Array.isArray(content.tokenAddress)
          ? content.tokenAddress
          : typeof content.tokenAddress === 'string'
            ? content.tokenAddress.split(',').map((addr: string) => addr.trim())
            : [];
      }

      const first = Math.min(
        typeof content.first === 'string'
          ? Number.parseInt(content.first, 10)
          : content.first || 20,
        50
      );

      const variables = {
        subjects: [content.address],
        perspective,
        first,
        after: content.after,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
      };

      logger.info('📡 Making GraphQL request to Zapper API');

      // 6. Execute query using GraphQL client
      const response = await client.query(query, variables);

      if (ZapperGraphQLUtils.hasErrors(response)) {
        const errorMessage = response.errors
          ? ZapperGraphQLUtils.formatGraphQLErrors(response.errors)
          : 'Unknown GraphQL error';
        logger.error('❌ GraphQL errors in response', { errors: response.errors });
        throw new Error(`GraphQL errors: ${errorMessage}`);
      }

      // 7. Process response data safely
      const responseData = response.data?.transactionHistoryV2;

      if (!responseData || !responseData.edges || responseData.edges.length === 0) {
        logger.warn('⚠️ No transaction history found', { address: content.address });

        callback?.({
          text: `No transaction history found for address ${content.address}.`,
          content: {
            address: content.address,
            perspective,
            message: 'No data found',
            totalCount: 0,
            transactionCount: 0,
          },
        });
        return true;
      }

      logger.info('✅ Data found, processing results', {
        resultCount: responseData.edges.length,
      });

      // 8. Format the response data
      const formattedResponse = processTransactionData(responseData, content.address, perspective);

      logger.info('✅ Response formatted successfully', {
        address: formattedResponse.address,
        transactionCount: formattedResponse.transactionCount,
      });

      const responseText = generateTransactionResponseText(formattedResponse);

      callback?.({
        text: responseText,
        content: formattedResponse,
      });

      logger.info('✅ GET_TRANSACTION_HISTORY handler completed successfully');
      return true;
    } catch (error) {
      logger.error('❌ Error in GET_TRANSACTION_HISTORY handler', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      callback?.({
        text: `Unable to get transaction history: ${errorMessage}`,
        content: { error: errorMessage },
      });

      return false;
    }
  },
  examples: [
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Show me transaction history for 0x1234567890abcdef1234567890abcdef12345678',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 25 transactions with total activity across multiple networks',
          action: 'GET_TRANSACTION_HISTORY',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Get recent transactions for vitalik.eth',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found 20 recent transactions for vitalik.eth',
          action: 'GET_TRANSACTION_HISTORY',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Show transaction history without an address',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'I need a wallet address to get transaction history. Please provide an Ethereum address, ENS name, or Basename.',
          action: 'GET_TRANSACTION_HISTORY',
        },
      },
    ],
  ] as ActionExample[][],
};

function isTransactionHistoryContent(
  _runtime: IAgentRuntime,
  content: Partial<TransactionHistoryContent>
): content is TransactionHistoryContent {
  logger.debug('🔍 isTransactionHistoryContent: Starting validation');

  // Check if content exists
  if (!content) {
    logger.error('❌ Validation failed: content is null/undefined');
    return false;
  }

  // Check address
  if (typeof content.address !== 'string' || content.address.length === 0) {
    logger.error('❌ Validation failed: address is not a non-empty string');
    return false;
  }

  logger.log('✅ isTransactionHistoryContent: All validation checks passed');
  return true;
}

// ✅ Safe data processing
function processTransactionData(
  data: TransactionHistoryResponse['transactionHistoryV2'],
  address: string,
  perspective: TransactionPerspective
): {
  address: string;
  perspective: TransactionPerspective;
  totalCount: number;
  transactionCount: number;
  hasMore: boolean;
  endCursor?: string;
  transactions: Array<{
    hash: string;
    timestamp: string;
    network: string;
    fromAddress?: string;
    toAddress?: string;
    value: string;
    description: string;
  }>;
} {
  const transactions = data.edges.map(
    (edge: {
      node: {
        hash: string;
        timestamp: string;
        network: string;
        fromAddress?: { address: string };
        toAddress?: { address: string };
        value: string;
        interpretation?: { processedDescription?: string; description?: string };
      };
      cursor: string;
    }) => {
      const node = edge.node;
      return {
        hash: node.hash || '',
        timestamp: node.timestamp || '',
        network: node.network || '',
        fromAddress: node.fromAddress?.address,
        toAddress: node.toAddress?.address,
        value: node.value || '0',
        description:
          node.interpretation?.processedDescription ||
          node.interpretation?.description ||
          'Transaction',
      };
    }
  );

  return {
    address,
    perspective,
    totalCount: data.totalCount || 0,
    transactionCount: transactions.length,
    hasMore: data.pageInfo?.hasNextPage || false,
    endCursor: data.pageInfo?.endCursor,
    transactions,
  };
}

function generateTransactionResponseText(data: {
  address: string;
  totalCount: number;
  transactionCount: number;
  hasMore: boolean;
  transactions: Array<{ timestamp: string; description: string; value: string }>;
}): string {
  if (data.transactionCount === 0) {
    return `No transaction history found for ${data.address}`;
  }

  const recentTransactions = data.transactions.slice(0, 5);
  const transactionSummary = recentTransactions
    .map((tx, index) => {
      const timeAgo = getTimeAgo(new Date(tx.timestamp));
      const valueText = tx.value !== '0' ? ` (${formatValue(tx.value)} ETH)` : '';
      return `${index + 1}. ${timeAgo}: ${tx.description}${valueText}`;
    })
    .join('\n');

  return `Transaction History for ${data.address}:

📊 Found ${data.totalCount} transactions (showing ${data.transactionCount})

Recent Transactions:
${transactionSummary}

${data.hasMore ? '\n📄 More transactions available for pagination.' : ''}`;
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds}s ago`;
  }
  if (diffInSeconds < 3600) {
    return `${Math.floor(diffInSeconds / 60)}m ago`;
  }
  if (diffInSeconds < 86400) {
    return `${Math.floor(diffInSeconds / 3600)}h ago`;
  }
  return `${Math.floor(diffInSeconds / 86400)}d ago`;
}

function formatValue(value: string): string {
  const num = Number.parseFloat(value);
  if (Number.isNaN(num)) return '0';

  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return num.toFixed(4);
}
